import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";

// Client workspaces live at growthifye.com/{company}/… — resolve the first path segment before the router boots
const RESERVED = new Set(["", "api", "static", "admin", "login", "teamportal", "team-portal", "hq", "client-portal", "client-login", "clientportal", "services", "coreservices", "solutions", "insights", "intel", "tenders", "tender", "ppp", "ppp-intel", "energy-map", "state", "states", "real-estate", "news", "events", "team", "impact", "faq", "digest", "archive", "topics", "search", "careers", "gallery", "contactus", "contact-us", "aboutus", "approach", "sitemap", "terms", "privacy-policy", "privacypolicy", "termsandconditions", "market-pulse", "marketpulse", "market-prices", "bess-sizing", "besssizing", "ebooks", "casestudies", "case-studies", "alerts", "preferences", "digest-prefs", "how-we-save", "life-at-growthifye", "compare", "vendor-quote", "vault-upload", "broker-quote", "matrix-signoff", "exit-survey", "vendor-hub", "supplier-portal", "agreement-review", "agreement-sign", "client-view", "sign", "interview", "meet", "upload"]);
window.__gfBasename = "";
window.__gfTenantSlug = "";
window.gfPath = (p) => `${window.__gfBasename}${p}`;
const resolveTenantPrefix = async () => {
  const seg = (window.location.pathname.split("/")[1] || "").toLowerCase();
  if (RESERVED.has(seg) || !/^[a-z0-9][a-z0-9-]{2,40}$/.test(seg)) return;
  try {
    const r = await _origFetch(`${process.env.REACT_APP_BACKEND_URL}/api/saas/workspace/resolve/${seg}`);
    const d = r.ok ? await r.json() : { ok: false };
    if (!d.ok) return;
    window.__gfBasename = `/${d.slug}`;
    window.__gfTenantSlug = d.type === "software_only" ? d.slug : "";
    window.__gfTenantInfo = d;
    if (d.type === "software_only") {
      const er = await _origFetch(`${process.env.REACT_APP_BACKEND_URL}/api/saas/workspace/enter/${d.slug}`, { method: "POST", credentials: "include", headers: { "X-Requested-With": "XMLHttpRequest", "X-Gf-Tenant": d.slug } });
      if (!er.ok) { const e = await er.json().catch(() => ({})); window.__gfTenantError = e.detail || "Workspace not available"; }
    }
    const rest = window.location.pathname.slice(seg.length + 1);
    if (rest === "" || rest === "/") window.history.replaceState(null, "", `/${d.slug}${d.home}${window.location.search}${window.location.hash}`);
  } catch { /* fall back to Growthifye routes */ }
};

// CSRF: stamp all mutating API calls with X-Requested-With (enforced server-side on /api/team + /api/admin); tenant header for workspace routing
const _origFetch = window.fetch.bind(window);
window.fetch = (input, init) => {
  if (typeof input === "string" && input.includes("/api/")) {
    const method = ((init && init.method) || "GET").toUpperCase();
    init = { ...(init || {}) };
    const headers = new Headers(init.headers || {});
    if (method !== "GET" && method !== "HEAD" && !headers.has("X-Requested-With")) headers.set("X-Requested-With", "XMLHttpRequest");
    if (window.__gfTenantSlug && !headers.has("X-Gf-Tenant")) headers.set("X-Gf-Tenant", window.__gfTenantSlug);
    init.headers = headers;
  }
  return _origFetch(input, init);
};

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import { applyTheme } from "@/lib/theme";
import { initI18n } from "@/lib/i18n";
import "@/lib/leafletA11y";
applyTheme();
setInterval(applyTheme, 30 * 60 * 1000);
initI18n();
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
resolveTenantPrefix().then(() => {
  if (window.__gfTenantSlug) axios.defaults.headers.common["X-Gf-Tenant"] = window.__gfTenantSlug;
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  );
});
